import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";

type UseTextTruncationResult = {
	isTruncated: boolean;
	isExpanded: boolean;
	toggleExpanded: () => void;
};

export const useTextTruncation = <T extends readonly unknown[]>(
	textRef: RefObject<HTMLElement | null>,
	deps: T = [] as unknown as T,
): UseTextTruncationResult => {
	const [isTruncated, setIsTruncated] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	const toggleExpanded = useCallback(() => {
		setIsExpanded((prev) => !prev);
	}, []);

	useEffect(() => {
		const checkOverflow = () => {
			const element = textRef.current;
			if (element && !isExpanded) {
				setIsTruncated(element.scrollHeight > element.clientHeight);
			}
		};

		checkOverflow();
		window.addEventListener("resize", checkOverflow);

		return () => {
			window.removeEventListener("resize", checkOverflow);
		};
	}, [textRef, isExpanded, ...deps]);

	return { isTruncated, isExpanded, toggleExpanded };
};
